"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useEvidence = void 0;
const react_1 = require("react");
const react_router_dom_1 = require("react-router-dom");
const lib_1 = require("use-debounce/lib");
const useMediaQuery_1 = __importDefault(require("@material-ui/core/useMediaQuery"));
const evidence_defaults_context_1 = require("../evidence-defaults/evidence-defaults-context");
const navigation_context_1 = require("../navigation/navigation-context");
const split_screen_types_1 = require("../../components/split-screen/split-screen-types");
const crud_source_hook_1 = require("../../utils/crud-source-hook");
const empty_component_1 = require("../../utils/empty-component");
const event_callback_hook_1 = require("../../utils/event-callback-hook");
const scrollable_source_hook_1 = require("../../utils/scrollable-source-hook");
const evidence_types_1 = require("./evidence-types");
const hooked_source_hook_1 = require("./hook/hooked-source-hook");
const item_redirect_hook_1 = require("./hook/item-redirect-hook");
function useEvidence({ tableProps, detailProps, apiProps, }) {
    var _a, _b, _c, _d, _e;
    (0, item_redirect_hook_1.useEvidenceItemRedirect)();
    const route = (0, react_router_dom_1.useRouteMatch)();
    const { id } = (0, react_router_dom_1.useParams)();
    const { search } = (0, react_router_dom_1.useLocation)();
    const params = new URLSearchParams(search);
    const { splitScreen: defaultSplitScreenMode } = (0, react_1.useContext)(evidence_defaults_context_1.EvidenceDefaultsContext);
    const { hookedSources, refreshAllHookedSources, resetAllHookedSources } = (0, hooked_source_hook_1.useHookedSources)(apiProps.hookedSources);
    const tableSource = (0, scrollable_source_hook_1.useScrollableSource)({
        url: apiProps.url + '/list',
        listItems: apiProps.listItems,
    });
    const crudSource = (0, crud_source_hook_1.useCrudSource)({
        url: apiProps.url,
        getItem: apiProps.getItem,
        createItem: apiProps.createItem,
        updateItem: apiProps.updateItem,
        deleteItem: apiProps.deleteItem,
        handleCreateError: apiProps.handleCreateError,
        handleUpdateError: apiProps.handleUpdateError,
        handleGetError: apiProps.handleGetError,
        handleDeleteError: apiProps.handleDeleteError,
        createMessages: apiProps.createMessages,
        updateMessages: apiProps.updateMessages,
        getMessages: apiProps.getMessages,
        delMessages: apiProps.delMessages,
        refreshAllHookedSources,
        resetAllHookedSources,
    });
    const tableRef = (0, react_1.useRef)(null);
    const detailRef = (0, react_1.useRef)(null);
    const splitScreenRef = (0, react_1.useRef)(null);
    const { name: appName } = (0, react_1.useContext)(evidence_defaults_context_1.EvidenceDefaultsContext);
    const { stateAction, navigate } = (0, react_1.useContext)(navigation_context_1.NavigationContext);
    (0, react_1.useEffect)(() => {
        var _a;
        if ((stateAction === null || stateAction === void 0 ? void 0 : stateAction.action) === evidence_types_1.EvidenceStateAction.NEW_ITEM) {
            // needs to do in next frame after Detail component is rendered
            requestAnimationFrame(() => {
                var _a;
                (_a = detailRef.current) === null || _a === void 0 ? void 0 : _a.startNew(stateAction.data);
            });
        }
        if ((stateAction === null || stateAction === void 0 ? void 0 : stateAction.action) === evidence_types_1.EvidenceStateAction.SHOW_ITEM) {
            requestAnimationFrame(() => {
                var _a;
                (_a = tableRef.current) === null || _a === void 0 ? void 0 : _a.setActiveRow(stateAction.data);
            });
        }
        if ((stateAction === null || stateAction === void 0 ? void 0 : stateAction.action) === evidence_types_1.EvidenceStateAction.REFRESH_TABLE) {
            (_a = tableRef.current) === null || _a === void 0 ? void 0 : _a.refresh();
        }
    }, [stateAction]);
    (0, react_1.useEffect)(() => {
        var _a;
        if (!id) {
            (_a = tableRef.current) === null || _a === void 0 ? void 0 : _a.setActiveRow(null);
            handleActiveRowChange(null);
        }
    }, [id]);
    const handleRowClick = (0, event_callback_hook_1.useEventCallback)((id) => {
        var _a, _b;
        (_a = tableProps === null || tableProps === void 0 ? void 0 : tableProps.onRowClick) === null || _a === void 0 ? void 0 : _a.call(tableProps, id);
        if (id !== ((_b = tableRef.current) === null || _b === void 0 ? void 0 : _b.activeRow)) {
            const query = params.toString();
            navigate(route.path.replace(':id', id) + query, false, {
                action: evidence_types_1.EvidenceStateAction.SHOW_ITEM,
                data: id,
            });
        }
    });
    const handleActiveRowChange = (0, event_callback_hook_1.useEventCallback)((id) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        (_a = detailRef.current) === null || _a === void 0 ? void 0 : _a.setActive(id);
        (_c = (_b = detailRef.current) === null || _b === void 0 ? void 0 : _b.formRef) === null || _c === void 0 ? void 0 : _c.resetValidation();
        const isFullscreenTable = (_d = splitScreenRef.current) === null || _d === void 0 ? void 0 : _d.isLeftOnFullscreen();
        if (isFullscreenTable) {
            if (((_e = splitScreenRef.current) === null || _e === void 0 ? void 0 : _e.midPoint) === window.innerWidth) {
                (_f = splitScreenRef.current) === null || _f === void 0 ? void 0 : _f.handleFullSizeRight();
            }
            else {
                (_g = splitScreenRef.current) === null || _g === void 0 ? void 0 : _g.handleMoveToMiddle();
            }
        }
        (_h = tableProps === null || tableProps === void 0 ? void 0 : tableProps.onActiveChange) === null || _h === void 0 ? void 0 : _h.call(tableProps, id);
    });
    const [handleActiveRowChangeDebounced] = (0, lib_1.useDebouncedCallback)(handleActiveRowChange, 500);
    const handleDetailPersisted = (0, event_callback_hook_1.useEventCallback)((id) => {
        var _a, _b, _c;
        (_a = tableRef.current) === null || _a === void 0 ? void 0 : _a.refresh();
        (_b = tableRef.current) === null || _b === void 0 ? void 0 : _b.setActiveRow(id);
        if (!id) {
            navigate(route.path.replace(':id', ''), true);
        }
        else if (id !== ((_c = tableRef.current) === null || _c === void 0 ? void 0 : _c.activeRow)) {
            const query = params.toString();
            navigate(route.path.replace(':id', id) + query, false, {
                action: evidence_types_1.EvidenceStateAction.SHOW_ITEM,
                data: id,
            });
        }
    });
    const columns = (_a = tableProps === null || tableProps === void 0 ? void 0 : tableProps.columns) !== null && _a !== void 0 ? _a : [];
    const FieldsComponent = (_b = detailProps === null || detailProps === void 0 ? void 0 : detailProps.FieldsComponent) !== null && _b !== void 0 ? _b : empty_component_1.EmptyComponent;
    const defaultTitle = (0, react_1.useRef)(appName !== null && appName !== void 0 ? appName : document.title);
    const detailTitle = (0, react_1.useMemo)(() => {
        var _a, _b;
        let tabTitle = '';
        if (detailProps === null || detailProps === void 0 ? void 0 : detailProps.documentTitle) {
            return typeof (detailProps === null || detailProps === void 0 ? void 0 : detailProps.documentTitle) === 'string'
                ? detailProps === null || detailProps === void 0 ? void 0 : detailProps.documentTitle
                : typeof (detailProps === null || detailProps === void 0 ? void 0 : detailProps.documentTitle) === 'function'
                    ? detailProps === null || detailProps === void 0 ? void 0 : detailProps.documentTitle(crudSource.data)
                    : '';
        }
        if ((_a = detailProps === null || detailProps === void 0 ? void 0 : detailProps.toolbarProps) === null || _a === void 0 ? void 0 : _a.title) {
            const title = detailProps.toolbarProps.title;
            tabTitle =
                typeof title === 'string'
                    ? title
                    : typeof title === 'function'
                        ? title(crudSource.data)
                        : '';
        }
        if ((_b = detailProps === null || detailProps === void 0 ? void 0 : detailProps.toolbarProps) === null || _b === void 0 ? void 0 : _b.subtitle) {
            const subtitle = detailProps.toolbarProps.subtitle;
            const subtitleText = typeof subtitle === 'string'
                ? subtitle
                : typeof subtitle === 'function'
                    ? subtitle(crudSource.data)
                    : '';
            tabTitle = subtitleText ? `${tabTitle} - ${subtitleText}` : tabTitle;
        }
        return tabTitle;
    }, [
        crudSource.data,
        (_c = detailProps === null || detailProps === void 0 ? void 0 : detailProps.toolbarProps) === null || _c === void 0 ? void 0 : _c.subtitle,
        (_d = detailProps === null || detailProps === void 0 ? void 0 : detailProps.toolbarProps) === null || _d === void 0 ? void 0 : _d.title,
        detailProps === null || detailProps === void 0 ? void 0 : detailProps.documentTitle,
    ]);
    const tableTitle = (_e = tableProps === null || tableProps === void 0 ? void 0 : tableProps.tableName) !== null && _e !== void 0 ? _e : document.title;
    const isSmall = (0, useMediaQuery_1.default)('(max-width:960px)');
    return {
        columns,
        FieldsComponent,
        tableSource,
        crudSource,
        hookedSources,
        tableRef,
        splitScreenRef,
        detailRef,
        handleRowClick,
        handleActiveRowChange: handleActiveRowChangeDebounced,
        handleDetailPersisted,
        defaultTitle,
        detailTitle,
        tableTitle,
        defaultSplitScreenMode: 
        // combined Table (when id is not specified) - Detail (when id is specified) view
        defaultSplitScreenMode === split_screen_types_1.SplitScreenMode.LEFT && id
            ? split_screen_types_1.SplitScreenMode.RIGHT
            : isSmall
                ? split_screen_types_1.SplitScreenMode.LEFT
                : defaultSplitScreenMode,
    };
}
exports.useEvidence = useEvidence;
//# sourceMappingURL=evidence-hook.js.map